// React hook that drives ON-SELECT, LIVE ChiDesk availability for the faceted
// spa panel. It owns NO cache — every generation is a FRESH set of live
// available_times calls. A "generation" is the tuple (day, therapistGuid,
// selected-treatment-set): whenever ANY of those changes the previous
// generation is cancelled, its results are discarded, and new calls are fired.
// A cached slot could already be booked, so we never serve a stored past result.
//
// UNION MODEL (therapist = "Any available", the default):
//   For each selected treatment we fire ONE available_times call PER bookable
//   therapist (chidesk.staff → each row's chidesk_id), through the shared
//   limiter (concurrency 4, ~140ms min-gap → under ChiDesk's 8/sec cap). As each
//   therapist resolves we MERGE its slots into the treatment's union: dedupe by
//   ChiDesk's exact `start` string and union the free-therapist ids (each call's
//   slots are attributed to the therapist we queried). Times therefore appear
//   PROGRESSIVELY as therapists resolve.
//
// SPECIFIC-THERAPIST MODEL (guest picked a chip):
//   therapistGuid is set → exactly ONE call per treatment (that therapist only).
//
// FAIL OPEN: a therapist erroring / returning [] just contributes nothing to the
// union. A treatment is only "definitively none" when EVERY therapist returned a
// clean empty (zero errors). If any therapist errored and the union is empty, the
// result is UNKNOWN → surfaced as a Retry-able error, never a hard block.
//
// TZ SAFETY: slot `start` strings are ChiDesk's fake-Z (wall-clock is local) and
// are never passed through new Date(); all merge/dedupe is on the literal string.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { WidgetChideskTreatment } from "../types";
import { fetchChideskDaySlots, type ChideskDaySlots, type ChideskSlot } from "./chidesk";
import { CANCELLED, createChideskLimiter } from "./chidesk-availability";

export interface ChideskLiveAvailabilityHook {
  /** undefined = not selected (never fetched); 'loading' = fetching, nothing yet;
   *  else the LIVE merged result for the current generation. */
  slotsFor: (rowId: string) => ChideskDaySlots | "loading" | undefined;
  /** Union fetch progress across every pending therapist call, or null when idle
   *  / fully resolved. Powers the "checking therapists — X of Y" hint. */
  progress: { done: number; total: number } | null;
  /** True while any therapist call for the current generation is still in flight. */
  fetching: boolean;
  /** Re-run the current generation from scratch (fresh live calls). */
  retry: () => void;
  version: number;
}

interface UnionAcc {
  byStart: Map<string, ChideskSlot>; // dedupe by exact ChiDesk `start`
  total: number; // therapist calls expected for this treatment
  settled: number; // therapist calls resolved (ok OR errored)
  errored: number; // therapist calls that failed / were UNKNOWN
}

// Merge one therapist's slots into a treatment's union accumulator. Each slot is
// attributed to `guid` (the therapist we queried) so the union's employeeIds ends
// up listing every therapist free at that exact start.
function mergeSlot(byStart: Map<string, ChideskSlot>, slot: ChideskSlot, guid: string | null): void {
  const existing = byStart.get(slot.start);
  if (!existing) {
    const ids = new Set<string>(slot.employeeIds);
    if (guid) ids.add(guid);
    byStart.set(slot.start, { ...slot, employeeIds: [...ids] });
    return;
  }
  const ids = new Set<string>(existing.employeeIds);
  for (const e of slot.employeeIds) ids.add(e);
  if (guid) ids.add(guid);
  existing.employeeIds = [...ids];
}

/**
 * @param integrationId  tenant_integrations.id forwarded to chidesk-proxy
 * @param day            'YYYY-MM-DD' — the selected day
 * @param treatments     the SELECTED treatment chain (drives what we fetch)
 * @param staffGuids     bookable therapist chidesk_ids for the union; [] → one
 *                       "any" call per treatment (tenant has no picker / no staff)
 * @param therapistGuid  a specific therapist's chidesk_id, or null for the union
 * @param tzOffsetMinutes tenant UTC-offset for the ChiDesk-UTC → tenant-local
 *                        slot conversion (SAST = 120)
 */
export function useChideskLiveAvailability(
  integrationId: string,
  day: string,
  treatments: WidgetChideskTreatment[],
  staffGuids: string[],
  therapistGuid: string | null,
  tzOffsetMinutes = 120,
): ChideskLiveAvailabilityHook {
  const unionRef = useRef<Map<string, UnionAcc>>(new Map());
  const aliveRef = useRef<{ v: boolean }>({ v: false });
  const limiterRef = useRef(createChideskLimiter());
  const [version, setVersion] = useState(0);
  const [nonce, setNonce] = useState(0); // retry → new generation

  const bump = useCallback(() => setVersion((v) => v + 1), []);

  // Stable generation keys (never depend on array identity).
  const treatmentsKey = useMemo(
    () => treatments.map((t) => t.id).sort().join(","),
    [treatments],
  );
  const staffKey = useMemo(() => [...staffGuids].sort().join(","), [staffGuids]);
  const treatmentsRef = useRef(treatments);
  treatmentsRef.current = treatments;
  const staffRef = useRef(staffGuids);
  staffRef.current = staffGuids;

  useEffect(() => {
    // ── New generation: cancel + discard everything from the last one ──────────
    aliveRef.current.v = false;
    limiterRef.current.sweepCancelled();
    const alive = { v: true };
    aliveRef.current = alive;

    const currentTreatments = treatmentsRef.current;
    if (currentTreatments.length === 0) {
      unionRef.current = new Map();
      bump();
      return () => {
        alive.v = false;
        limiterRef.current.sweepCancelled();
      };
    }

    // "Any" → one call per bookable therapist; a specific pick or an empty staff
    // list → a single "any" call (null therapist). Never zero targets.
    const targets: (string | null)[] = therapistGuid
      ? [therapistGuid]
      : staffRef.current.length > 0
        ? [...staffRef.current]
        : [null];

    const union = new Map<string, UnionAcc>();
    for (const t of currentTreatments) {
      union.set(t.id, { byStart: new Map(), total: targets.length, settled: 0, errored: 0 });
    }
    unionRef.current = union;
    bump();

    for (const t of currentTreatments) {
      for (const g of targets) {
        void (async () => {
          const res = await limiterRef.current
            .run(
              () =>
                fetchChideskDaySlots({
                  tenantIntegrationId: integrationId,
                  treatmentGuid: t.chidesk_id,
                  day,
                  staffGuid: g,
                  tzOffsetMinutes,
                }),
              () => !alive.v,
            )
            .catch<ChideskDaySlots>(() => ({ ok: false, error: "availability_check_failed" }));
          if (!alive.v) return; // superseded generation — drop the result
          if (res === CANCELLED) return; // never dispatched
          const acc = union.get(t.id);
          if (!acc) return;
          acc.settled += 1;
          if (!res.ok) acc.errored += 1;
          else for (const slot of res.slots) mergeSlot(acc.byStart, slot, g);
          bump();
        })();
      }
    }

    return () => {
      alive.v = false;
      limiterRef.current.sweepCancelled();
    };
    // treatmentsKey/staffKey stand in for the (identity-unstable) arrays.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrationId, day, therapistGuid, treatmentsKey, staffKey, tzOffsetMinutes, nonce, bump]);

  const slotsFor = useCallback(
    (rowId: string): ChideskDaySlots | "loading" | undefined => {
      void version; // recompute as the union merges
      const acc = unionRef.current.get(rowId);
      if (!acc) return undefined; // not selected → never fetched
      const hasSlots = acc.byStart.size > 0;
      const done = acc.settled >= acc.total;
      if (!done && !hasSlots) return "loading"; // fetching, nothing resolved yet
      if (hasSlots) {
        const slots = [...acc.byStart.values()].sort((a, b) => a.minuteOfDay - b.minuteOfDay);
        const byMinute = new Map<number, ChideskSlot>();
        for (const s of slots) if (!byMinute.has(s.minuteOfDay)) byMinute.set(s.minuteOfDay, s);
        return { ok: true, slots, byMinute };
      }
      // done && no slots:
      //   • every therapist returned a CLEAN empty → definitive none for the day
      //   • any therapist errored → UNKNOWN (fail open, Retry-able) — NOT "none"
      if (acc.errored > 0) return { ok: false, error: "availability_check_failed" };
      return { ok: true, slots: [], byMinute: new Map() };
    },
    [version],
  );

  const progress = useMemo(() => {
    void version;
    let total = 0;
    let done = 0;
    for (const acc of unionRef.current.values()) {
      total += acc.total;
      done += acc.settled;
    }
    return total > 0 && done < total ? { done, total } : null;
  }, [version]);

  const retry = useCallback(() => setNonce((n) => n + 1), []);

  return { slotsFor, progress, fetching: progress != null, retry, version };
}
